import React, { useState, useEffect } from "react";
import {
  Search, Trash2, Sparkles, X, Copy, Film as FilmIcon,
  Music, Loader2, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, fmtBytes, fmtDuration, faceImageUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";
import { BulkUploader } from "@/components/ingest/BulkUploader";
import { UrlImport } from "@/components/ingest/UrlImport";
import { LocalFolderImport } from "@/components/ingest/LocalFolderImport";
import { useDesktop } from "@/hooks/useDesktop";
import { StorageWarning, useStorage } from "@/components/settings/StorageMeter";
import { ProductionPanel } from "@/components/ingest/ProductionPanel";

const QUALITIES = ["UNRATED", "KEEP", "GOOD", "REVIEW", "POOR", "REJECTED"];
const FILTERS = ["ALL", "KEEP", "GOOD", "REVIEW", "POOR", "REJECTED", "UNRATED"];

function StatusPill({ status }) {
  const map = {
    imported: ["#E6B04E", "Queued"], scanned: ["#00F0FF", "Scanned"],
    ready: ["#10B981", "Ready"],
  };
  const [c, label] = map[status] || ["#64748B", status];
  return <span className="tech-badge" style={{ color: c }}>{label}</span>;
}

function MediaCard({ item, onOpen }) {
  const hasThumb = !!item.storage_thumbnail;
  return (
    <div
      data-testid={`media-card-${item.id}`}
      onClick={() => onOpen(item)}
      className="studio-card overflow-hidden cursor-pointer group hover:border-[#3B4259] transition-all"
    >
      <div className="relative aspect-video bg-[#0c0e14] flex items-center justify-center overflow-hidden">
        {hasThumb ? (
          <img src={assetUrl(item.id, "thumbnail")} alt={item.filename} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : item.kind === "audio" ? (
          <Music size={30} className="text-[#334155]" />
        ) : item.status === "imported" ? (
          <Loader2 size={24} className="text-[#E6B04E] animate-spin" />
        ) : (
          <FilmIcon size={28} className="text-[#334155]" />
        )}
        {item.duration > 0 && (
          <span className="absolute bottom-2 right-2 tech-badge">{fmtDuration(item.duration)}</span>
        )}
        {item.duplicate_of && (
          <span className="absolute top-2 left-2 tech-badge" style={{ color: "#fbbf24" }} title="Duplicate detected"><Copy size={10} className="inline mr-1" />DUP</span>
        )}
        <span className="absolute top-2 right-2"><StatusPill status={item.status} /></span>
      </div>
      <div className="p-3">
        <div className="text-[13px] font-medium truncate mb-1.5" title={item.filename}>{item.filename}</div>
        <div className="flex items-center justify-between">
          <span className={`chip chip-${item.quality_category}`}>{item.quality_category}</span>
          <span className="metric-label" style={{ fontSize: 9 }}>
            {item.resolution || "—"} {item.fps ? `· ${item.fps}fps` : ""}
          </span>
        </div>
      </div>
    </div>
  );
}

function Intelligence({ item, onChange }) {
  const [events, setEvents] = useState([]);
  const [emotions, setEmotions] = useState([]);
  const [tx, setTx] = useState(null);
  const [faces, setFaces] = useState([]);

  useEffect(() => {
    api.eventsCatalog().then((d) => { setEvents(d.events); setEmotions(d.emotions); }).catch(() => {});
    if (item.has_transcript) api.transcript(item.id).then(setTx).catch(() => setTx(null));
    else setTx(null);
    if (item.face_count) api.mediaFaces(item.id).then((d) => setFaces(d.faces)).catch(() => {});
    else setFaces([]);
  }, [item.id, item.has_transcript, item.face_count]);

  const q = item.quality;
  const saveTag = async (patch) => {
    try { await api.updateTags(item.id, patch); onChange({ ...item, ...patch }); toast.success("Updated"); }
    catch (e) { toast.error("Failed"); }
  };

  const Bar = ({ label, val }) => (
    <div>
      <div className="flex justify-between metric-label mb-1"><span>{label}</span><span>{val ?? "—"}</span></div>
      <div className="progress-track"><div className="h-full rounded-full" style={{ width: `${val || 0}%`, background: "linear-gradient(90deg,#E6B04E,#00F0FF)" }} /></div>
    </div>
  );

  return (
    <div className="space-y-5">
      {q && (
        <div className="studio-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="metric-label">Quality Scores</div>
            <span className={`chip chip-${q.auto_category}`}>AUTO: {q.auto_category} · {item.final_score}</span>
          </div>
          <div className="space-y-2.5">
            <Bar label="Sharpness" val={q.sharpness_score} />
            <Bar label="Exposure" val={q.exposure_score} />
            <Bar label="Stability" val={q.stability_score} />
            <Bar label="Noise" val={q.noise_score} />
          </div>
          {q.flags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">{q.flags.map((f) => <span key={f} className="chip chip-REVIEW">{f}</span>)}</div>
          )}
        </div>
      )}

      <div className="studio-card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="metric-label">Wedding Intelligence</div>
          {item.tagged_by && <span className="tech-badge">via {item.tagged_by}</span>}
        </div>
        {item.scene_desc && <p className="text-sm text-[#CBD5E1] italic">"{item.scene_desc}"</p>}
        <div>
          <label className="metric-label block mb-1">Event</label>
          <select data-testid="edit-event" className="studio-input !py-1.5 text-sm" value={item.wedding_event || ""} onChange={(e) => saveTag({ wedding_event: e.target.value })}>
            <option value="">— unset —</option>
            {events.map((e) => <option key={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label className="metric-label block mb-1">Emotion</label>
          <select data-testid="edit-emotion" className="studio-input !py-1.5 text-sm" value={item.emotion || ""} onChange={(e) => saveTag({ emotion: e.target.value })}>
            <option value="">— unset —</option>
            {emotions.map((e) => <option key={e}>{e}</option>)}
          </select>
        </div>
        {item.semantic_tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">{item.semantic_tags.map((t) => <span key={t} className="tech-badge">{t}</span>)}</div>
        )}
        <label className="flex items-center gap-2 text-xs text-[#94A3B8] pt-1">
          <input type="checkbox" data-testid="lock-tags" checked={!!item.tags_locked} onChange={(e) => saveTag({ tags_locked: e.target.checked })} />
          Lock tags (prevent AI overwrite)
        </label>
      </div>

      {faces.length > 0 && (
        <div className="studio-card p-4">
          <div className="metric-label mb-2">Detected Faces ({faces.length})</div>
          <div className="flex flex-wrap gap-2">
            {faces.map((f) => (
              <img key={f.id} src={faceImageUrl(f.id)} alt="face" className="w-12 h-12 rounded-lg object-cover bg-[#0c0e14]" />
            ))}
          </div>
        </div>
      )}

      {tx && (
        <div className="studio-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="metric-label">Transcript · {tx.language} · {tx.speaker_count} speaker(s)</div>
            <span className="tech-badge">{tx.filler_count} fillers</span>
          </div>
          {tx.segments?.length ? (
            <div className="font-mono text-[11px] space-y-1 max-h-48 overflow-y-auto">
              {tx.segments.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-[#475569]">{fmtDuration(s.start)}</span>
                  <span className="text-[#E6B04E]">{s.speaker}</span>
                  <span className="text-[#CBD5E1]">{s.text}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-[#64748B] text-xs">No speech detected in this clip.</p>}
        </div>
      )}
    </div>
  );
}

function Inspector({ item, onClose, onChange }) {
  const [analyzing, setAnalyzing] = useState(false);
  if (!item) return null;
  const meta = item.metadata || {};

  const setQuality = async (q) => {
    try { await api.setQuality(item.id, q); onChange({ ...item, quality_category: q }); toast.success(`Tagged ${q}`); }
    catch (e) { toast.error("Failed to update"); }
  };
  const del = async () => {
    if (!window.confirm("Remove this clip from the project?")) return;
    try { await api.deleteMedia(item.id); toast.success("Clip removed"); onClose(true); }
    catch (e) { toast.error("Delete failed"); }
  };
  const analyze = async () => {
    setAnalyzing(true);
    try { const r = await api.aiAnalyze(item.id); onChange({ ...item, ai_analysis: r.analysis, ai_provider: r.provider }); toast.success(`Analyzed via ${r.provider}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI analysis failed"); }
    finally { setAnalyzing(false); }
  };

  const Row = ({ k, v }) => (
    <div className="flex justify-between py-1.5 border-b border-[#252A3B] last:border-0">
      <span className="metric-label">{k}</span>
      <span className="font-mono text-[12px] text-[#CBD5E1] text-right">{v ?? "—"}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/60" onClick={() => onClose(false)}>
      <div data-testid="media-inspector" className="w-full max-w-md h-full bg-[#12141C] border-l border-[#252A3B] overflow-y-auto fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#252A3B] sticky top-0 bg-[#12141C] z-10">
          <h3 className="font-head font-bold text-lg truncate pr-4">{item.filename}</h3>
          <button data-testid="close-inspector" onClick={() => onClose(false)} className="text-[#64748B] hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          {item.storage_proxy ? (
            <video data-testid="inspector-proxy" src={assetUrl(item.id, "proxy")} controls className="w-full rounded-lg bg-black" />
          ) : item.storage_thumbnail ? (
            <img src={assetUrl(item.id, "thumbnail")} alt="" className="w-full rounded-lg" />
          ) : (
            <div className="aspect-video rounded-lg bg-[#0c0e14] flex items-center justify-center text-[#475569] text-sm">Proxy generating…</div>
          )}

          {item.storage_waveform && (
            <div>
              <div className="metric-label mb-1.5">Audio Waveform</div>
              <img src={assetUrl(item.id, "waveform")} alt="waveform" className="w-full rounded-lg bg-[#0c0e14]" />
            </div>
          )}

          <div>
            <div className="metric-label mb-2">Quality Tag</div>
            <div className="flex flex-wrap gap-2">
              {QUALITIES.map((q) => (
                <button key={q} data-testid={`quality-${q}`} onClick={() => setQuality(q)}
                  className={`chip chip-${q} ${item.quality_category === q ? "ring-2 ring-offset-1 ring-offset-[#12141C] ring-current" : "opacity-60 hover:opacity-100"}`}>
                  {q}
                </button>
              ))}
            </div>
          </div>

          <div className="studio-card p-4">
            <div className="metric-label mb-2">Technical Metadata</div>
            <Row k="Status" v={item.status} />
            <Row k="Resolution" v={meta.resolution} />
            <Row k="FPS" v={meta.fps} />
            <Row k="Codec" v={meta.codec || meta.audio_codec} />
            <Row k="Container" v={meta.container} />
            <Row k="Duration" v={fmtDuration(item.duration)} />
            <Row k="Size" v={fmtBytes(item.size_bytes)} />
            <Row k="Audio" v={meta.has_audio ? `${meta.channels || "?"}ch @ ${meta.sample_rate || "?"}Hz` : "none"} />
            <Row k="Aspect" v={meta.aspect_ratio} />
            <Row k="Scan" v={meta.interlaced ? "Interlaced" : "Progressive"} />
            <Row k="Hash" v={item.content_hash ? item.content_hash.slice(0, 12) : "—"} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="metric-label">AI Frame Analysis</div>
              <button data-testid="btn-ai-analyze" className="btn btn-ghost !py-1.5 !px-3 text-[12px]" onClick={analyze} disabled={analyzing || !item.storage_thumbnail}>
                {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Analyze
              </button>
            </div>
            {item.ai_analysis ? (
              <pre className="studio-card p-3 text-[11px] font-mono text-[#94A3B8] whitespace-pre-wrap break-words max-h-52 overflow-y-auto">{item.ai_analysis}</pre>
            ) : (
              <div className="text-[#64748B] text-xs">Run Gemini vision analysis on this clip's keyframe.</div>
            )}
          </div>

          <Intelligence item={item} onChange={onChange} />

          <button data-testid="btn-delete-media" className="btn btn-danger w-full" onClick={del}><Trash2 size={15} /> Remove Clip</button>
        </div>
      </div>
    </div>
  );
}

export default function Media() {
  const { projectId, project } = useApp();
  const desktop = useDesktop();
  const storageInfo = useStorage();
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const { data, refresh } = usePoll(
    () => (projectId ? api.listMedia(projectId, { limit: 120, quality: filter, search }) : Promise.resolve(null)),
    3000, [projectId, filter, search]
  );

  if (!projectId) return <NoProject title="Import media into a project" />;

  const items = data?.items || [];

  return (
    <div className="p-8 fade-up space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Media Library</h1>
          <p className="text-[#94A3B8] text-sm mt-1">{data?.total ?? 0} clips · {project?.name}</p>
        </div>
      </div>

      <ProductionPanel projectId={projectId} />

      {/* import zone */}
      <StorageWarning s={storageInfo} />
      {desktop?.desktop && <LocalFolderImport projectId={projectId} onDone={refresh} />}
      <div className="grid xl:grid-cols-[1.6fr_1fr] gap-4">
        <BulkUploader projectId={projectId} onDone={refresh} />
        <UrlImport projectId={projectId} onDone={refresh} />
      </div>

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input data-testid="media-search" className="studio-input pl-9" placeholder="Search filename…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`quality-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
              className={`chip ${f === "ALL" ? "chip-UNRATED" : `chip-${f}`} ${filter === f ? "ring-2 ring-offset-1 ring-offset-[#090A0F] ring-current" : "opacity-55 hover:opacity-100"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* grid */}
      {items.length === 0 ? (
        <div className="studio-card p-12 text-center text-[#64748B]">
          <AlertCircle size={28} className="mx-auto mb-3 text-[#334155]" />
          No media {filter !== "ALL" ? `tagged ${filter}` : "yet"}. Import footage above.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((it) => <MediaCard key={it.id} item={it} onOpen={setSelected} />)}
        </div>
      )}

      <Inspector item={selected} onClose={() => { setSelected(null); refresh(); }} onChange={setSelected} />
    </div>
  );
}
